import React, { createContext, useContext, useState, useEffect } from 'react';
import type { User, AuthState } from '../types/user';

interface AuthContextType extends AuthState {
  login: (credentials: any) => Promise<void>;
  register: (data: any) => Promise<void>;
  logout: () => void;
  updateProfile: (data: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    // Simulate checking for existing session
    const timer = setTimeout(() => {
      const savedUser = localStorage.getItem('keskedo_user');
      if (savedUser) {
        setState({
          user: JSON.parse(savedUser),
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        setState((prev: AuthState) => ({ ...prev, isLoading: false }));
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const login = async (credentials: { email?: string }) => {
    setState((prev: AuthState) => ({ ...prev, isLoading: true }));
    // Mock login
    const mockUser: User = {
      id: '1',
      username: 'keskedo_pioneer',
      email: credentials.email || 'user@example.com',
      reputation: 750,
      connectionsCount: 124,
      circlesCount: 8,
      avatarUrl: 'https://picsum.photos/seed/keskedo/200',
    };
    localStorage.setItem('keskedo_user', JSON.stringify(mockUser));
    setState({
      user: mockUser,
      isAuthenticated: true,
      isLoading: false,
    });
  };

  const register = async (data: { username: string; email: string }) => {
    setState((prev: AuthState) => ({ ...prev, isLoading: true }));
    // Mock register
    const mockUser: User = {
      id: '2',
      username: data.username,
      email: data.email,
      reputation: 100,
      connectionsCount: 0,
      circlesCount: 0,
    };
    localStorage.setItem('keskedo_user', JSON.stringify(mockUser));
    setState({
      user: mockUser,
      isAuthenticated: true,
      isLoading: false,
    });
  };

  const logout = () => {
    localStorage.removeItem('keskedo_user');
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  const updateProfile = (data: Partial<User>) => {
    if (state.user) {
      const updatedUser = { ...state.user, ...data };
      localStorage.setItem('keskedo_user', JSON.stringify(updatedUser));
      setState((prev: AuthState) => ({ ...prev, user: updatedUser }));
    }
  };

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
